import { memo, useMemo } from "react";
import { Dialog } from "cerulean";
import { useLocalization } from "@fluent/react";
import {
  Button,
  Radio,
  ResponsiveFlex,
  Row,
  TextInput,
} from "@narmi/design_system";
import type { DialogProps } from "@narmi/design_system/dist/types/Dialog";
import { formAdapters } from "../../src";
import AccountSelector from "../AccountSelector";
import { useDirectDepositManual } from "./DirectDepositContainer";
import { useInstitutionSettings } from "../contexts/InstitutionSettingsContext";
import { useCurrentUser } from "../contexts/CurrentUserContext";
import styles from "./DirectDepositLandingPage.module.scss";

type DirectDepositManualFormProps = Pick<
  DialogProps,
  "isOpen" | "onUserDismiss"
>;

const DirectDepositManualFormSuccess = memo(() => {
  const { l10n } = useLocalization();
  const { currentUser } = useCurrentUser();
  const { long_name: institutionName } = useInstitutionSettings();
  const { email: emailAddress = "" } = currentUser || {};

  return (
    <ResponsiveFlex gapSize="l">
      <div className="fontSize--s fontColor--secondary">
        {l10n.getString(
          "direct-deposit-manual-success-subtitle",
          { institutionName },
          `To authorize direct deposits to your ${institutionName} account, please submit this form to your employer’s payroll department.`
        )}
      </div>
      {l10n.getElement(
        <div className="fontSize--s fontColor--secondary" />,
        "direct-deposit-manual-success-subtitle-email",
        {
          vars: { emailAddress },
          elems: { email: <strong>{emailAddress}</strong> },
        }
      )}
    </ResponsiveFlex>
  );
});

const DirectDepositManualForm = ({
  isOpen,
  onUserDismiss,
}: DirectDepositManualFormProps) => {
  const {
    form,
    selectedAllocationMethod,
    eligibleAccounts,
    accountFieldFormProps,
    isSubmitFormLoading,
    isFormComplete,
    submitFormHandler,
    downloadFormHandler,
  } = useDirectDepositManual(onUserDismiss);
  const { l10n } = useLocalization();

  const FormTitle = useMemo(
    () =>
      l10n.getString(
        "direct-deposit-manual-form-title",
        {},
        "Additional Information"
      ),
    [l10n]
  );
  const SuccessTitle = useMemo(
    () =>
      l10n.getString(
        "direct-deposit-manual-success-title",
        {},
        "Your direct deposit form is ready"
      ),
    [l10n]
  );

  const FormFooter = useMemo(
    () => (
      <Row justifyContent="end">
        <Button
          size="m"
          kind="primary"
          isLoading={isSubmitFormLoading}
          onClick={submitFormHandler}
          label={l10n.getString(
            "direct-deposit-manual-form-button-continue",
            {},
            "Continue"
          )}
        />
      </Row>
    ),
    [isSubmitFormLoading, submitFormHandler, l10n]
  );
  const SuccessFooter = useMemo(
    () => (
      <Row justifyContent="end" gapSize="s">
        <Button
          size="m"
          kind="secondary"
          onClick={onUserDismiss}
          label={l10n.getString(
            "direct-deposit-manual-success-button-close",
            {},
            "Close"
          )}
        />
        <Button
          size="m"
          kind="primary"
          onClick={downloadFormHandler}
          label={l10n.getString(
            "direct-deposit-manual-success-button-download-form",
            {},
            "Download form"
          )}
        />
      </Row>
    ),
    [onUserDismiss, downloadFormHandler, l10n]
  );

  return (
    <Dialog
      isOpen={isOpen}
      onUserDismiss={onUserDismiss}
      title={isFormComplete ? SuccessTitle : FormTitle}
      footer={isFormComplete ? SuccessFooter : FormFooter}
    >
      {isFormComplete ? (
        <DirectDepositManualFormSuccess />
      ) : (
        <ResponsiveFlex gapSize="l">
          <div className="fontSize--s fontColor--secondary">
            {l10n.getString(
              "direct-deposit-manual-form-subtitle",
              {},
              "We need to following information to help build your direct deposit form."
            )}
          </div>
          <div className={styles.accountSelectorContainer}>
            <AccountSelector
              isDestination={false}
              field="account"
              label={l10n.getString(
                "direct-deposit-account-drawer-title",
                {},
                "Account"
              )}
              showAddExternalAccountLink={false}
              accounts={eligibleAccounts}
              {...accountFieldFormProps}
            />
          </div>
          <TextInput
            label="Employer"
            {...formAdapters.input(form, "employer")}
          />
          <div className={styles.radioOptionsContainer}>
            <Radio
              {...formAdapters.radioSingle(form, "allocationMethod", "whole")}
            >
              {l10n.getString(
                "direct-deposit-manual-form-allocation-option-entire",
                {},
                "Entire paycheck"
              )}
            </Radio>
            <div className="border--top" />
            <Radio
              {...formAdapters.radioSingle(
                form,
                "allocationMethod",
                "percentage"
              )}
            >
              {l10n.getString(
                "direct-deposit-manual-form-allocation-option-percentage",
                {},
                "Percentage (%)"
              )}
              {selectedAllocationMethod === "percentage" && (
                <div className="padding--top--m">
                  <TextInput
                    inputMode="decimal"
                    label={l10n.getString(
                      "direct-deposit-manual-form-allocation-option-percentage-label",
                      {},
                      "Percentage allocation"
                    )}
                    {...formAdapters.input(form, "valuePercent")}
                  />
                </div>
              )}
            </Radio>
            <div className="border--top" />
            <Radio
              {...formAdapters.radioSingle(form, "allocationMethod", "cents")}
            >
              {l10n.getString(
                "direct-deposit-manual-form-allocation-option-value",
                {},
                "Value ($)"
              )}
              {selectedAllocationMethod === "cents" && (
                <div className="padding--top--m">
                  <TextInput
                    inputMode="decimal"
                    label={l10n.getString(
                      "direct-deposit-manual-form-allocation-option-value-label",
                      {},
                      "Value allocation"
                    )}
                    {...formAdapters.amountInput(form, "valueCents")}
                  />
                </div>
              )}
            </Radio>
          </div>
        </ResponsiveFlex>
      )}
    </Dialog>
  );
};

export default DirectDepositManualForm;
